import { LocalizedText } from '@/client/components/localized-text';
import { UserAvatar } from '@/client/components/user-avatar';
import type { ActivityTrade } from '@/client/hooks/use-recent-trades';
import { cn } from '@/client/utils';
import { OrderSide } from '@/libs/sdk/api';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';

import { fmtShares, sharesWei, toCents } from './activity-format';

// One broadcast line, shared by the desktop footer bar and the mobile toast:
//   {avatar} {name} bought {shares} {Yes/No} @ {price}¢ · {market}
// The outcome word is coloured; the market name truncates so the row stays one
// line at any width.
export const ActivityLine = ({ trade }: { trade: ActivityTrade }) => {
  const { t } = useTranslation();
  const { option } = trade;
  const isBuy = trade.side === OrderSide.deposit;
  // Positive side is resolved upstream the same way the rest of the app does,
  // so Up / Higher / Yes all read green and Down / Lower / No read red.
  const isPositive = trade.isPrimaryOutcome;
  const cents = toCents(trade.dealPrice, option.decimal);
  const shares = fmtShares(sharesWei(trade), option.decimal);

  return (
    <Link
      href={`/market/${trade.marketAddress}`}
      className="flex items-center gap-1.5 whitespace-nowrap text-xs hover:opacity-80 transition-opacity"
    >
      <UserAvatar
        avatar={trade.userAvatarUrl}
        fallback={trade.userName.slice(0, 1).toUpperCase()}
        className="size-4"
      />
      {/* bdi: user/market strings are content in their own script — without
          isolation they bleed direction into the line under RTL locales. */}
      <bdi className="font-medium text-foreground/85 max-w-28 truncate">{trade.userName}</bdi>
      <span className="text-muted-foreground">{isBuy ? t('bought', 'bought') : t('sold', 'sold')}</span>
      <span className="font-mono text-foreground/85">{shares}</span>
      <span className={cn('font-semibold', isPositive ? 'text-beneficial-foreground' : 'text-destructive')}>
        {option.name}
      </span>
      {cents !== null && <span className="font-mono text-foreground/50">@ {cents}¢</span>}
      <span className="text-muted-foreground/60">·</span>
      <bdi className="text-foreground/60 max-w-56 truncate">
        <LocalizedText i18n={trade.marketNameI18n}>{trade.marketName}</LocalizedText>
      </bdi>
    </Link>
  );
};
